/**
 * Capital Planning — Terra-driven portfolio allocation.
 *
 * Replaces the legacy risk-tier optimizer. For each site in the portfolio,
 * the backend gathers three cost drivers (DW install, 24mo remediation,
 * plume expansion penalty) and asks Gemini to allocate the requested budget.
 *
 * Returns:
 *   - Funded sites with explicit allocation + per-site rationale + drivers.
 *   - Deferred sites with a one-sentence reason.
 *   - A global notes paragraph explaining the strategy.
 */
import { useState } from 'react';
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { polygonApi } from '@/api/polygon';
import type { CapitalRecommendationResponse } from '@/api/polygon';
import { formatCurrency } from '@/utils/format';

function formatBig(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

export function CapitalPlanning() {
  const [budget, setBudget] = useState('5000000');
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [maxSites, setMaxSites] = useState('');
  const [minAllocation, setMinAllocation] = useState('50000');

  const [recommendation, setRecommendation] = useState<CapitalRecommendationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFunded, setExpandedFunded] = useState<Set<string>>(new Set());
  const [expandedDeferred, setExpandedDeferred] = useState<Set<string>>(new Set());

  const toggleFunded = (id: string) => {
    setExpandedFunded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleDeferred = (id: string) => {
    setExpandedDeferred(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleRecommend = async () => {
    setError(null);
    setLoading(true);
    setRecommendation(null);
    try {
      const result = await polygonApi.recommendCapitalAllocation({
        total_budget: parseFloat(budget),
        fiscal_year: fiscalYear,
        max_sites: maxSites ? parseInt(maxSites, 10) : undefined,
        min_allocation: minAllocation ? parseFloat(minAllocation) : undefined,
      });
      setRecommendation(result);
      // Default state: collapse everything for quick scan
      setExpandedFunded(new Set());
      setExpandedDeferred(new Set());
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Allocation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-mono-800 pb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium text-mono-100 tracking-widest flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-cyan-400" />
            ALLOCATION
          </h1>
          <p className="text-mono-500 text-xs mt-1">
            Terra allocates your budget across the portfolio based on DW install, remediation, and plume expansion costs per site.
          </p>
        </div>
      </div>

      {/* Parameters */}
      <div className="border border-mono-800">
        <div className="border-b border-mono-800 px-4 py-3">
          <h3 className="text-xs text-mono-300 tracking-widest">ALLOCATION PARAMETERS</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-[10px] text-mono-500 tracking-widest mb-2">TOTAL BUDGET</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mono-500 text-xs">$</span>
                <input
                  type="number"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-xs focus:border-mono-500 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] text-mono-500 tracking-widest mb-2">FISCAL YEAR</label>
              <select
                value={fiscalYear}
                onChange={(e) => setFiscalYear(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-xs focus:border-mono-500 focus:outline-none"
              >
                {[2024, 2025, 2026, 2027, 2028].map((year) => (
                  <option key={year} value={year}>FY {year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-mono-500 tracking-widest mb-2">MAX SITES (OPTIONAL)</label>
              <input
                type="number"
                value={maxSites}
                onChange={(e) => setMaxSites(e.target.value)}
                placeholder="No limit"
                className="w-full px-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-xs focus:border-mono-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] text-mono-500 tracking-widest mb-2">MIN ALLOCATION / SITE</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mono-500 text-xs">$</span>
                <input
                  type="number"
                  value={minAllocation}
                  onChange={(e) => setMinAllocation(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-xs focus:border-mono-500 focus:outline-none"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleRecommend}
              disabled={loading}
              className={`flex items-center gap-2 px-4 py-2 text-xs tracking-widest border transition-colors ${
                loading
                  ? 'border-mono-700 text-mono-500 cursor-not-allowed'
                  : 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/15'
              }`}
            >
              {loading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <SparklesIcon className="w-4 h-4" />}
              {loading ? 'TERRA THINKING...' : 'RUN TERRA ALLOCATION'}
            </button>
            {error && <span className="text-red-400 text-xs">{error}</span>}
          </div>
        </div>
      </div>

      {/* Results */}
      {recommendation && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
              <div className="text-[10px] text-mono-500 uppercase">Total Budget</div>
              <div className="text-sm font-semibold text-mono-100">{formatBig(recommendation.total_budget)}</div>
            </div>
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
              <div className="text-[10px] text-mono-500 uppercase">Allocated</div>
              <div className="text-sm font-semibold text-cyan-400">{formatBig(recommendation.total_allocated)}</div>
            </div>
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
              <div className="text-[10px] text-mono-500 uppercase">Unallocated</div>
              <div className="text-sm font-semibold text-mono-100">{formatBig(recommendation.unallocated)}</div>
            </div>
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
              <div className="text-[10px] text-mono-500 uppercase">Funded / Deferred</div>
              <div className="text-sm font-semibold text-mono-100">
                {recommendation.funded.length} / {recommendation.deferred.length}
              </div>
            </div>
          </div>

          {/* Strategy notes */}
          {recommendation.notes && (
            <div className="border border-cyan-500/30 bg-cyan-500/5 rounded-lg p-4">
              <div className="text-[10px] text-cyan-400 uppercase tracking-wider mb-2">Strategy</div>
              <p className="text-mono-200 text-sm leading-relaxed">{recommendation.notes}</p>
            </div>
          )}

          {/* Funded sites */}
          <div className="border border-mono-800 rounded-lg">
            <div className="border-b border-mono-800 px-4 py-3 flex items-center justify-between">
              <h3 className="text-xs text-mono-300 tracking-widest">FUNDED ({recommendation.funded.length})</h3>
              <span className="text-[10px] text-mono-500">Click a site to expand drivers</span>
            </div>
            <div className="divide-y divide-mono-800">
              {recommendation.funded.length === 0 ? (
                <div className="p-4 text-mono-500 text-sm text-center">No sites funded.</div>
              ) : (
                recommendation.funded.map(f => {
                  const expanded = expandedFunded.has(f.site_id);
                  const pct = recommendation.total_budget > 0
                    ? (f.amount / recommendation.total_budget) * 100
                    : 0;
                  return (
                    <div key={f.site_id}>
                      <button
                        onClick={() => toggleFunded(f.site_id)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-mono-900/40 text-left"
                      >
                        {expanded
                          ? <ChevronDownIcon className="w-4 h-4 text-mono-500 flex-shrink-0" />
                          : <ChevronRightIcon className="w-4 h-4 text-mono-500 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-mono-100 text-sm font-medium truncate">{f.site_name}</div>
                          <div className="text-mono-500 text-[10px] truncate">{f.rationale}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-cyan-400 text-sm font-semibold">{formatCurrency(f.amount)}</div>
                          <div className="text-mono-500 text-[10px]">{pct.toFixed(1)}% of budget</div>
                        </div>
                      </button>
                      {expanded && (
                        <div className="px-10 pb-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          <DriverPill label="DW Install" value={f.drivers.dw_install_cost} accent="amber" />
                          <DriverPill label="Remediation 24mo" value={f.drivers.remediation_24mo_cost} accent="cyan" />
                          <DriverPill label="Plume Expansion Penalty" value={f.drivers.plume_expansion_cost} accent="red" />
                          <TrendPill value={f.drivers.concentration_growth_pct} />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Deferred sites */}
          {recommendation.deferred.length > 0 && (
            <div className="border border-mono-800 rounded-lg">
              <div className="border-b border-mono-800 px-4 py-3 flex items-center justify-between">
                <h3 className="text-xs text-mono-300 tracking-widest">DEFERRED ({recommendation.deferred.length})</h3>
                <span className="text-[10px] text-mono-500">Not funded this cycle</span>
              </div>
              <div className="divide-y divide-mono-800">
                {recommendation.deferred.map(d => {
                  const expanded = expandedDeferred.has(d.site_id);
                  return (
                    <div key={d.site_id}>
                      <button
                        onClick={() => toggleDeferred(d.site_id)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-mono-900/40 text-left"
                      >
                        {expanded
                          ? <ChevronDownIcon className="w-4 h-4 text-mono-500 flex-shrink-0" />
                          : <ChevronRightIcon className="w-4 h-4 text-mono-500 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-mono-200 text-sm font-medium truncate">{d.site_name}</div>
                          <div className="text-mono-500 text-[10px] truncate">{d.reason}</div>
                        </div>
                      </button>
                      {expanded && (
                        <div className="px-10 pb-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          <DriverPill label="DW Install" value={d.drivers.dw_install_cost} accent="amber" />
                          <DriverPill label="Remediation 24mo" value={d.drivers.remediation_24mo_cost} accent="cyan" />
                          <DriverPill label="Plume Expansion Penalty" value={d.drivers.plume_expansion_cost} accent="red" />
                          <TrendPill value={d.drivers.concentration_growth_pct} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DriverPill({ label, value, accent }: { label: string; value: number; accent: 'amber' | 'cyan' | 'red' }) {
  const colors: Record<string, string> = {
    amber: 'border-amber-500/30 text-amber-400',
    cyan: 'border-cyan-500/30 text-cyan-400',
    red: 'border-red-500/30 text-red-400',
  };
  return (
    <div className={`bg-mono-900 border rounded p-2 ${colors[accent]}`}>
      <div className="text-[9px] text-mono-500 uppercase tracking-wider">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{formatBig(value)}</div>
    </div>
  );
}

function TrendPill({ value }: { value: number }) {
  // Concentration growth, decimal pct. Positive = rising contamination → urgent.
  const pct = value * 100;
  let border = 'border-mono-700';
  let text = 'text-mono-300';
  let label = 'STABLE';
  if (pct > 25) { border = 'border-red-500/40'; text = 'text-red-400'; label = 'RISING FAST'; }
  else if (pct > 5) { border = 'border-amber-500/40'; text = 'text-amber-400'; label = 'RISING'; }
  else if (pct < -5) { border = 'border-emerald-500/40'; text = 'text-emerald-400'; label = 'DECLINING'; }
  const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '→';
  return (
    <div className={`bg-mono-900 border rounded p-2 ${border} ${text}`}>
      <div className="text-[9px] text-mono-500 uppercase tracking-wider">Concentration Trend</div>
      <div className="text-sm font-semibold mt-0.5 flex items-center gap-1">
        <span>{arrow}</span>
        <span>{pct > 0 ? '+' : ''}{pct.toFixed(1)}%</span>
        <span className="text-[9px] tracking-wider opacity-80">{label}</span>
      </div>
    </div>
  );
}
